import { useTranslation } from 'react-i18next'
import { ArrowUp, ArrowDown, Search } from 'lucide-react'
import { Input, Select, Button } from '@/components/ui'
import { Tabs } from '@/components/ui/Tabs'
import type { SelectOption } from '@/components/ui/Select'

export interface SharedOwnerFiltersProps {
  usageStatus: string
  onUsageStatusChange: (value: string) => void
  search: string
  onSearchChange: (value: string) => void
  propertyId: string
  onPropertyIdChange: (value: string) => void
  propertyOptions: SelectOption[]
  sort: string
  order: 'asc' | 'desc'
  onSortChange: (key: string) => void
  onToggleOrder: () => void
  sortOptions: SelectOption[]
}

/**
 * Co-owner list filter bar (EP-111 T111-9, D11). Status Tabs + a full-width
 * label-less search, then a "Recent" heading with the property scope (the
 * global useSelectedPropertyStore picker, per the designer's "one source of
 * truth" note — the caller wires propertyId/onPropertyIdChange to that store)
 * and sort controls — mirrors CustomerFilters' layout.
 *
 * TABS-ONLY (D11) — unlike CustomerFilters, there is deliberately NO duplicate
 * status Select next to the sort controls. The 2026-07-30 Customers redundancy
 * (Tabs + Select both bound to the same status) is an accepted transitional
 * state for THAT page (project_redesigned_pages_transitional) — it must not
 * be copied here.
 *
 * Renamed from CoOwnerFilters by EP-133 SS4b-2 (2026-08-20).
 */
export function SharedOwnerFilters({
  usageStatus,
  onUsageStatusChange,
  search,
  onSearchChange,
  propertyId,
  onPropertyIdChange,
  propertyOptions,
  sort,
  order,
  onSortChange,
  onToggleOrder,
  sortOptions,
}: SharedOwnerFiltersProps) {
  const { t } = useTranslation('shared_owners')
  const { t: tc } = useTranslation('common')

  const orderLabel = order === 'asc' ? tc('sort.order_asc') : tc('sort.order_desc')

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        {/* Status tabs — segmented control, the ONLY status filter control
            on this page (D11 tabs-only). */}
        <Tabs
          variant="bordered"
          value={usageStatus}
          onChange={onUsageStatusChange}
          options={[
            { value: '', label: t('tabs.all') },
            { value: 'owner_occupied', label: t('tabs.owner_occupied') },
            { value: 'available_for_rent', label: t('tabs.available_for_rent') },
            { value: 'rented', label: t('tabs.rented') },
            { value: 'for_sale', label: t('tabs.for_sale') },
          ]}
        />

        {/* Full-width, label-less search (mirrors CustomerFilters). */}
        <Input
          aria-label={tc('actions.search')}
          leftIcon={<Search className="w-4 h-4" />}
          placeholder={t('placeholders.search')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      {/* "Recent" heading + property scope + sort controls, above the grid. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl sm:text-2xl font-bold">{t('sections.recent')}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            data-testid="co-owner-property-select"
            aria-label={t('labels.property')}
            size="sm"
            value={propertyId}
            onChange={(e) => onPropertyIdChange(e.target.value)}
            options={propertyOptions}
            className="min-w-0"
          />
          <Select
            data-testid="co-owner-sort-select"
            aria-label={tc('sort.label')}
            size="sm"
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
            options={sortOptions}
            className="min-w-0"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={orderLabel}
            onClick={onToggleOrder}
            leftIcon={order === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
            className="shrink-0"
          >
            <span className="hidden sm:inline">{orderLabel}</span>
          </Button>
        </div>
      </div>
    </div>
  )
}
